import { FunctionComponent, MouseEvent } from 'react'
import { Icon } from '~/components/Icon'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { useToast } from '~/components/Toast/useToasts'
import { WithClassName } from '~/types/utils'
import { copyToClipboard } from '~/utils/string'

type CopyableIdProps = WithClassName & {
  id: string
  /** What the toast and the tooltip call the value — "Media ID", "Ad unit ID". */
  label: string
  trackingLabel: TrackingLabel
}

/**
 * An id the reader copies. Nobody types a UUID back in by hand, so the id is the control and clicking it
 * copies. The click stops here: in a list the row opens the media, and copying an id is not asking for
 * that.
 */
export const CopyableId: FunctionComponent<CopyableIdProps> = ({ id, label, trackingLabel, className }) => {
  const toast = useToast()

  const handleCopyClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    copyToClipboard(id)
    toast.success(
      <>
        <strong>{label}</strong> was successfully copied to your clipboard.
      </>
    )
  }

  return (
    <button
      type={'button'}
      className={'group flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-left'}
      title={`Copy ${label}`}
      onClick={handleCopyClick}
      data-tracking={trackingLabel}
    >
      {/* The caller's type goes here, not on the button. A global rule gives every button `font: inherit`,
          and it sits outside Tailwind's `utilities` layer — unlayered CSS wins whatever the specificity,
          so a `text-xs` on the button is dead. Nothing unlayered touches a span. */}
      <span className={className}>{id}</span>
      <Icon name={'copy'} width={'0.85em'} fill={'currentColor'} className={'opacity-0 transition-opacity group-hover:opacity-100'} />
    </button>
  )
}
